(function (scope) {
  'use strict';
  const AXES = ['length', 'width', 'height', 'price'];
  const PARAMS = AXES.flatMap(axis => ['min', 'max'].map(bound => `pw_${axis}_${bound}`));
  const MAX_PRODUCTS = 5000;
  function parseNumber(raw) {
    const text = String(raw ?? '').trim();
    if (!text) return null;
    if (!/^(?:\d+(?:[.,]\d{1,6})?|[.,]\d{1,6})$/.test(text)) throw new Error('invalid_number');
    const value = Number(text.replace(',', '.'));
    if (!Number.isFinite(value) || value < 0 || value > 1000000000) throw new Error('invalid_number');
    return value;
  }
  function parseState(values) {
    const state = {};
    const errors = {};
    for (const axis of AXES) {
      try {
        const min = parseNumber(values[`pw_${axis}_min`]);
        const max = parseNumber(values[`pw_${axis}_max`]);
        if (min !== null && max !== null && min > max) throw new Error('invalid_range');
        state[axis] = { min, max };
      } catch (error) { errors[axis] = error.message; }
    }
    return { state, errors };
  }
  function readURL(url) {
    const values = Object.fromEntries(PARAMS.map(key => [key, url.searchParams.get(key) ?? '']));
    return values;
  }
  function active(range) { return range && (range.min !== null || range.max !== null); }
  function hasFilters(state) { return AXES.some(axis => active(state[axis])); }
  function within(value, range) {
    if (!active(range)) return true;
    if (value === null || !Number.isFinite(value)) return false;
    const epsilon = Math.max(1, Math.abs(value), Math.abs(range.min ?? 0), Math.abs(range.max ?? 0)) * Number.EPSILON * 4;
    return (range.min === null || value + epsilon >= range.min) && (range.max === null || value - epsilon <= range.max);
  }
  function variantMatches(product, state) {
    return product.variants.some(variant => within(variant.price / 100, state.price));
  }
  function selectProducts(products, state) {
    const matches = [];
    let unknownExcluded = 0;
    for (const product of products) {
      if (!product.card.trim()) continue;
      if (!variantMatches(product, state)) continue;
      let unknown = false, fails = false;
      for (const axis of AXES.slice(0, 3)) {
        if (!active(state[axis])) continue;
        if (product[axis] === null) unknown = true;
        else if (!within(product[axis], state[axis])) fails = true;
      }
      if (fails) continue;
      if (unknown) { unknownExcluded++; continue; }
      matches.push(product);
    }
    return { matches, unknownExcluded };
  }
  function stateURL(original, state) {
    const url = new URL(original);
    PARAMS.forEach(key => url.searchParams.delete(key));
    url.searchParams.delete('filter.v.availability');
    url.searchParams.delete('pw_stock');
    url.searchParams.delete('page');
    for (const axis of AXES) for (const bound of ['min', 'max']) {
      if (state[axis][bound] !== null) url.searchParams.set(`pw_${axis}_${bound}`, String(state[axis][bound]));
    }
    return url;
  }
  function dataURL(original, origin, page = 1) {
    const url = new URL(original);
    PARAMS.forEach(key => url.searchParams.delete(key));
    url.searchParams.delete('filter.v.availability');
    url.searchParams.delete('pw_stock');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.searchParams.set('view', 'pw-filter-data');
    url.searchParams.set('page', String(page));
    if (origin === 'search') url.searchParams.set('type', 'product');
    return url;
  }
  function localizationURL(destination, current) {
    const source = new URL(current);
    const target = new URL(destination, source);
    const retired = new Set(['pw_stock', 'filter.v.availability']);
    const controlled = new Set([...PARAMS, ...retired, 'sort_by', 'q', 'type', 'page']);
    for (const key of [...source.searchParams.keys(), ...target.searchParams.keys()]) if (key.startsWith('options[')) controlled.add(key);
    controlled.forEach(key => target.searchParams.delete(key));
    for (const key of new Set(source.searchParams.keys())) {
      if (retired.has(key) || ['locale', 'locale_code', 'language', 'language_code'].includes(key)) continue;
      if (['country', 'country_code', 'region_country', 'currency'].includes(key) && target.hostname !== source.hostname) continue;
      if (controlled.has(key) || !target.searchParams.has(key)) source.searchParams.getAll(key).forEach(value => target.searchParams.append(key, value));
    }
    return target;
  }
  function syncLocalization(document, current) {
    const source = new URL(current);
    const allowed = new Set(['pawroom.se', 'pawroom.no', 'pawroom.dk', 'pawroom.fi']);
    for (const form of document.querySelectorAll('form#PawroomLanguage, form#PawroomFooterLanguage')) {
      let input = form.elements.namedItem('return_to');
      const target = localizationURL(input?.value || source.href, source);
      if (target.origin !== source.origin) continue;
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'return_to'; form.appendChild(input); }
      input.value = target.pathname + target.search + target.hash;
    }
    for (const link of document.querySelectorAll('.pw-locale-panel nav a[href], .pw-footer-selector-panel nav a[href]')) {
      const target = localizationURL(link.getAttribute('href'), source);
      if (target.protocol === 'https:' && allowed.has(target.hostname)) link.href = target.href;
    }
  }
  function validatePage(page, context, number, reference) {
    if (!page || page.schema !== 1 || page.release !== '20261009.1') throw new Error('invalid_snapshot');
    for (const key of ['origin', 'locale', 'country', 'currency', 'resource']) {
      if (String(page.context?.[key]) !== String(context[key])) throw new Error('mixed_context');
    }
    if (!Number.isInteger(page.total) || page.total < 0 || page.total > MAX_PRODUCTS || !Number.isInteger(page.pages) || page.pages < 0 || page.pages > 20 || page.page !== number || page.page_size !== 250 || page.page_param !== 'page') throw new Error('invalid_snapshot');
    if (page.pages !== Math.ceil(page.total / 250) || (reference && (page.total !== reference.total || page.pages !== reference.pages || page.sort !== reference.sort))) throw new Error('changed_snapshot');
    if (!Array.isArray(page.products) || page.products.length !== Math.min(250, Math.max(0, page.total - ((number - 1) * 250)))) throw new Error('incomplete_snapshot');
    for (const product of page.products) {
      if (!product || !/^[0-9]+$/.test(String(product.id)) || typeof product.card !== 'string') throw new Error('invalid_product');
      for (const axis of AXES.slice(0, 3)) if (product[axis] !== null && (typeof product[axis] !== 'number' || !Number.isFinite(product[axis]) || product[axis] <= 0)) throw new Error('invalid_measurement');
      if (!Array.isArray(product.variants) || !Number.isInteger(product.variant_count) || product.variant_count < 1 || product.variant_count > 250 || product.variants.length !== product.variant_count) throw new Error('incomplete_variants');
      const variants = new Set();
      for (const variant of product.variants) {
        if (!variant || !/^[0-9]+$/.test(String(variant.id)) || variants.has(String(variant.id)) || typeof variant.available !== 'boolean' || !Number.isSafeInteger(variant.price) || variant.price < 0) throw new Error('invalid_variant');
        variants.add(String(variant.id));
      }
    }
    return page;
  }
  function parseSnapshotResponse(raw) {
    const bytes = new TextEncoder().encode(raw).byteLength;
    if (bytes > 8000000) throw new Error('snapshot_too_large');
    return { page: JSON.parse(raw), bytes };
  }
  async function loadSnapshot(fetchPage, context, signal) {
    const products = [], ids = new Set();
    let reference, bytes = 0;
    for (let number = 1; number <= (reference ? Math.max(1, reference.pages) : 1); number++) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const response = await fetchPage(number, signal);
      if (!Number.isSafeInteger(response.bytes) || response.bytes < 0 || response.bytes > 8000000) throw new Error('snapshot_too_large');
      bytes += response.bytes;
      if (bytes > 40000000) throw new Error('snapshot_too_large');
      const page = validatePage(response.page, context, number, reference);
      reference ||= page;
      for (const product of page.products) {
        if (ids.has(String(product.id))) throw new Error('duplicate_product');
        ids.add(String(product.id)); products.push(product);
      }
    }
    if (products.length !== reference.total) throw new Error('incomplete_snapshot');
    return { products, created: Date.now(), bytes };
  }
  class LatestRequest {
    constructor() { this.generation = 0; this.controller = null; }
    next() { this.controller?.abort(); this.controller = new AbortController(); return { generation: ++this.generation, signal: this.controller.signal }; }
    current(request) { return request.generation === this.generation && !request.signal.aborted; }
  }
  const api = { AXES, PARAMS, parseNumber, parseState, readURL, hasFilters, within, variantMatches, selectProducts, stateURL, dataURL, localizationURL, validatePage, parseSnapshotResponse, loadSnapshot, LatestRequest };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!scope.document || scope.PawroomRanges) return;
  scope.PawroomRanges = api;
  const cache = new Map();
  function initialize(host) {
    if (host.dataset.pwRangeInitialized) return;
    host.dataset.pwRangeInitialized = '1';
    const incoming = new URL(location.href);
    if (incoming.searchParams.has('pw_stock') || incoming.searchParams.has('filter.v.availability')) {
      incoming.searchParams.delete('pw_stock'); incoming.searchParams.delete('filter.v.availability');
      location.replace(incoming.href); return;
    }
    const config = JSON.parse(host.querySelector('[data-pw-range-config]').textContent);
    const form = host.querySelector('[data-pw-range-form]');
    const native = host.querySelector('[data-pw-native-content]');
    const filtered = host.querySelector('[data-pw-filtered-content]');
    const grid = filtered.querySelector('ul');
    const status = host.querySelector('[data-pw-range-status]');
    const count = host.querySelector('[data-pw-range-count]');
    const chips = host.querySelector('[data-pw-range-chips]');
    const unknown = host.querySelector('[data-pw-range-unknown]');
    const empty = host.querySelector('[data-pw-range-empty]');
    const more = host.querySelector('[data-pw-range-more]');
    const activeCount = host.querySelector('[data-pw-range-active-count]');
    const sort = host.querySelector('[data-pw-range-sort]');
    const serverSort = Array.from(sort.options).find(option => option.defaultSelected)?.value || sort.value;
    const requests = new LatestRequest();
    const nativeFragment = document.createDocumentFragment();
    const originalURL = new URL(location.href);
    const originalResultsCount = host.closest('.collection-wrapper')?.getAttribute('data-standard-results-count');
    let applied, matches = [], shown = 0, detached = false;
    const numberFormat = new Intl.NumberFormat(config.context.locale, { maximumFractionDigits: 6 });
    const inputNumberFormat = new Intl.NumberFormat(config.context.locale, { maximumFractionDigits: 6, useGrouping: false });
    const text = (key, values = {}) => Object.entries(values).reduce((value, [name, replacement]) => value.replaceAll(`__${name.toUpperCase()}__`, replacement), config.messages[key]);
    function syncSort() {
      const value = new URL(location.href).searchParams.get('sort_by');
      const selected = Array.from(sort.options).some(option => option.value === value) ? value : serverSort;
      sort.value = selected;
      for (const option of sort.options) option.selected = option.value === selected;
      const wrapper = sort.parentElement;
      if (!wrapper?.classList.contains('select-wrapper')) return;
      const enhancer = wrapper.querySelector(':scope > .bv_mainselect');
      const label = enhancer?.querySelector('.bv_atual > .sl_inner');
      const option = Array.from(sort.options).find(option => option.selected);
      if (label && option) label.textContent = option.textContent.trim();
      const rows = Array.from(enhancer?.querySelectorAll('.bv_ul_inner > .li') || []).filter(row => row.querySelector(':scope > a'));
      if (rows.length !== sort.options.length) return;
      rows.forEach((row, index) => {
        const selected = sort.options[index].selected;
        row.classList.toggle('bv_selected', selected);
        if (row.getAttribute('aria-selected') !== null) row.setAttribute('aria-selected', String(selected));
      });
    }
    function fill(values) {
      for (const key of PARAMS) {
        const field = form.elements.namedItem(key);
        if (!field) continue;
        if (field.type === 'checkbox') field.checked = values[key] === '1';
        else {
          const raw = values[key] || '';
          try { const value = parseNumber(raw); field.value = value === null ? '' : inputNumberFormat.format(value); }
          catch { field.value = raw; }
        }
      }
    }
    function clearErrors() {
      form.querySelectorAll('[aria-invalid]').forEach(input => input.removeAttribute('aria-invalid'));
      form.querySelectorAll('[data-pw-range-error]').forEach(error => { error.textContent = ''; error.hidden = true; });
    }
    function validate(values) {
      clearErrors();
      const result = parseState(values);
      for (const [axis, error] of Object.entries(result.errors)) {
        const message = form.querySelector(`[data-pw-range-error="${axis}"]`);
        message.hidden = false; message.textContent = text(error);
        form.querySelectorAll(`[data-pw-axis="${axis}"]`).forEach(input => input.setAttribute('aria-invalid', 'true'));
      }
      if (Object.keys(result.errors).length) { form.querySelector('[aria-invalid]')?.focus(); return null; }
      return result.state;
    }
    function setBusy(busy) {
      form.querySelector('[type="submit"]').disabled = busy;
      host.setAttribute('aria-busy', String(busy));
      if (busy) status.textContent = text('loading');
    }
    function toggleNative(show) {
      if (show) grid.removeAttribute('id');
      if (show && detached) { native.appendChild(nativeFragment); detached = false; }
      if (!show && !detached) { while (native.firstChild) nativeFragment.appendChild(native.firstChild); detached = true; }
      if (!show) grid.id = 'collection';
      native.hidden = !show;
      filtered.hidden = show;
      host.closest('.collection-wrapper')?.toggleAttribute('data-pw-ranges-active', !show);
    }
    function restoreNativeResults() {
      toggleNative(true); grid.replaceChildren(); chips.replaceChildren(); activeCount.hidden = true; unknown.hidden = true; count.hidden = true;
      empty.hidden = true; more.hidden = true; matches = []; shown = 0;
      if (originalResultsCount !== null && originalResultsCount !== undefined) host.closest('.collection-wrapper')?.setAttribute('data-standard-results-count', originalResultsCount);
    }
    function initCards() {
      for (const event of ['ratings', 'createCols', 'listProductSlider']) scope.dispatchEvent(new CustomEvent(event));
      if (typeof ajaxCart !== 'undefined' && typeof ajaxCart.init === 'function') ajaxCart.init();
      if (typeof quickShop !== 'undefined' && typeof quickShop.init === 'function') quickShop.init();
      if (typeof saveLoadMoreAnchor === 'function') saveLoadMoreAnchor();
    }
    function appendCards() {
      const end = Math.min(matches.length, shown + config.pageSize);
      const fragment = document.createDocumentFragment();
      for (const product of matches.slice(shown, end)) {
        const template = document.createElement('template');
        template.innerHTML = product.card;
        const card = template.content.firstElementChild;
        if (!card || card.tagName !== 'LI' || template.content.children.length !== 1) throw new Error('invalid_card');
        card.dataset.pwProductId = String(product.id);
        fragment.appendChild(card);
      }
      grid.appendChild(fragment); shown = end;
      more.hidden = shown >= matches.length;
      initCards();
    }
    function showChips(state) {
      chips.replaceChildren();
      for (const axis of AXES) {
        const range = state[axis];
        if (!active(range)) continue;
        const label = config.labels[axis];
        const parts = [];
        if (range.min !== null) parts.push(`${config.messages.from} ${numberFormat.format(range.min)}`);
        if (range.max !== null) parts.push(`${config.messages.to} ${numberFormat.format(range.max)}`);
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'pw-range-chip'; button.dataset.pwRemove = axis;
        button.textContent = `${label}: ${parts.join(' · ')} ×`;
        button.setAttribute('aria-label', text('remove_filter', { filter: `${label}: ${parts.join(' · ')}` }));
        chips.appendChild(button);
      }
      activeCount.hidden = chips.children.length === 0;
      activeCount.textContent = `(${numberFormat.format(chips.children.length)})`;
    }
    async function snapshot(request) {
      const url = dataURL(location.href, config.context.origin);
      url.searchParams.delete('page');
      const key = `${JSON.stringify(config.context)}:${url}`;
      const previous = cache.get(key);
      if (previous && Date.now() - previous.created < 300000) return previous;
      const result = await loadSnapshot(async (page, signal) => {
        const controller = new AbortController();
        const abort = () => controller.abort();
        signal.addEventListener('abort', abort, { once: true });
        const timeout = setTimeout(abort, 30000);
        try {
          const endpoint = dataURL(location.href, config.context.origin, page);
          const response = await fetch(endpoint, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('load_failed');
          const raw = await response.text();
          return parseSnapshotResponse(raw);
        } finally { clearTimeout(timeout); signal.removeEventListener('abort', abort); }
      }, config.context, request.signal);
      if (!requests.current(request)) throw new DOMException('Aborted', 'AbortError');
      cache.set(key, result);
      while (cache.size > 3 || [...cache.values()].reduce((sum, item) => sum + item.bytes, 0) > 40000000) cache.delete(cache.keys().next().value);
      return result;
    }
    async function apply(state, historyMode = 'push') {
      const request = requests.next();
      if (!hasFilters(state)) {
        const clean = stateURL(location.href, state);
        const originalClean = stateURL(originalURL, state);
        if (clean.search !== originalClean.search || originalURL.searchParams.has('filter.v.availability')) { location.assign(clean.href); return; }
        restoreNativeResults();
        applied = state; status.textContent = ''; setBusy(false);
        if (historyMode === 'push') history.pushState({}, '', clean);
        else if (location.search !== clean.search) history.replaceState({}, '', clean);
        syncSort();
        syncLocalization(document, location.href);
        return;
      }
      setBusy(true);
      if (!applied) toggleNative(false);
      try {
        const data = await snapshot(request);
        if (!requests.current(request) || !host.isConnected) return;
        const result = selectProducts(data.products, state);
        const detachedGrid = document.createElement('template');
        for (const product of result.matches.slice(0, config.pageSize)) {
          detachedGrid.innerHTML = product.card;
          if (detachedGrid.content.children.length !== 1 || detachedGrid.content.firstElementChild?.tagName !== 'LI') throw new Error('invalid_card');
        }
        const nativeGrid = native.querySelector('#collection') || nativeFragment.querySelector('#collection');
        if (nativeGrid) grid.className = nativeGrid.className;
        toggleNative(false);
        matches = result.matches; shown = 0; grid.replaceChildren(); appendCards();
        applied = state; showChips(state);
        count.hidden = false; count.textContent = text(matches.length === 1 ? 'result_one' : 'result_other', { count: numberFormat.format(matches.length) });
        unknown.hidden = result.unknownExcluded === 0;
        empty.hidden = matches.length !== 0; status.textContent = '';
        host.closest('.collection-wrapper')?.setAttribute('data-standard-results-count', String(matches.length));
        if (historyMode === 'push') history.pushState({}, '', stateURL(location.href, state));
        else if (new URL(location.href).searchParams.has('pw_stock') || new URL(location.href).searchParams.has('filter.v.availability')) history.replaceState({}, '', stateURL(location.href, state));
        syncSort();
        syncLocalization(document, location.href);
      } catch (error) {
        if (!requests.current(request) || !host.isConnected) return;
        status.textContent = text('load_failed');
        if (!applied || !hasFilters(applied)) restoreNativeResults();
      } finally { if (requests.current(request) && host.isConnected) setBusy(false); }
    }
    const nativeGrid = native.querySelector('#collection');
    if (nativeGrid) new MutationObserver(() => { grid.className = nativeGrid.className; }).observe(nativeGrid, { attributes: true, attributeFilter: ['class'] });
    form.addEventListener('submit', event => {
      event.preventDefault();
      const values = Object.fromEntries(new FormData(form));
      const state = validate(values);
      if (state) apply(state);
    });
    host.addEventListener('click', event => {
      const reset = event.target.closest('[data-pw-range-reset]');
      const remove = event.target.closest('[data-pw-remove]');
      if (!reset && !remove) return;
      const values = reset ? {} : readURL(new URL(location.href));
      if (remove) { values[`pw_${remove.dataset.pwRemove}_min`] = ''; values[`pw_${remove.dataset.pwRemove}_max`] = ''; }
      fill(values); clearErrors();
      const state = parseState(values).state; apply(state);
    });
    more.addEventListener('click', () => { try { appendCards(); } catch { status.textContent = text('load_failed'); } });
    sort.addEventListener('change', () => setTimeout(() => {
      const url = new URL(location.href); url.searchParams.set('sort_by', sort.value); url.searchParams.delete('page'); location.assign(url.href);
    }, 0));
    scope.addEventListener('popstate', () => { if (!host.isConnected) return; syncSort(); syncLocalization(document, location.href); const values = readURL(new URL(location.href)); fill(values); const state = validate(values); if (state) apply(state, 'none'); });
    scope.addEventListener('pageshow', () => { if (!host.isConnected) return; syncSort(); syncLocalization(document, location.href); });
    syncSort();
    const initial = readURL(originalURL); fill(initial); const initialState = validate(initial);
    if (initialState && hasFilters(initialState)) { host.querySelector('details').open = true; apply(initialState, 'none'); }
  }
  function scan() { document.querySelectorAll('[data-pw-range-host]').forEach(initialize); syncLocalization(document, location.href); }
  document.addEventListener('submit', event => { if (['PawroomLanguage', 'PawroomFooterLanguage'].includes(event.target.id)) syncLocalization(document, location.href); }, true);
  document.addEventListener('change', event => { if (event.target.closest('#PawroomLanguage, #PawroomFooterLanguage')) syncLocalization(document, location.href); }, true);
  document.addEventListener('click', event => { if (event.target.closest('.pw-locale-panel nav a[href], .pw-footer-selector-panel nav a[href]')) syncLocalization(document, location.href); }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})(typeof window !== 'undefined' ? window : globalThis);
